import { useId, type ReactNode } from 'react';
import { cx } from '../cx';

export type ProgressProps = {
  value: number;
  max?: number;
  /** Visible label above the bar ("Profile complete"). Also its accessible name. */
  label: ReactNode;
  /** Visible value text at the end of the label row ("3 of 5"); also read out. */
  valueText?: string;
  className?: string;
};

/**
 * A bar for known progress. For short unknown waits use Spinner; when the shape of what is loading
 * is known, use Skeleton.
 */
export function Progress({ value, max = 100, label, valueText, className }: ProgressProps) {
  const labelId = `pg${useId().replace(/:/g, '')}`;
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span id={labelId} className="font-semibold text-jc-ink">
          {label}
        </span>
        {valueText ? <span className="text-jc-ink-secondary tabular-nums">{valueText}</span> : null}
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={valueText}
        aria-labelledby={labelId}
        className="h-2 overflow-hidden rounded-jc-info bg-jc-elevated"
      >
        <div
          className="h-full rounded-jc-info bg-jc-selected transition-all duration-(--jc-duration-slow) ease-jc-out motion-reduce:transition-none"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
